import React, { useMemo, useEffect, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import { useFormik } from 'formik';
import { parse, isValid, sub } from 'date-fns';
import { isTablet, isMobile } from 'react-device-detect';
import { FaCaretDown } from 'react-icons/fa';
import { DAYS, getMonths, YEARS } from './consts';
import validation from './validation';
import {
  Container,
  SelectContainer,
  DefaultSelectWrapper,
  Label,
  StyledSelect,
  DisabledDate
} from './styled';

const DateInput = forwardRef(
  (
    {
      variant,
      formik,
      name,
      showCaret = true,
      className,
      daysInPastLimit,
      availableDays = DAYS,
      availableYears = YEARS,
      disabled
    },
    ref
  ) => {
    const { setFieldValue, values } = formik;
    const months = getMonths(variant === 'small');

    const dateFormik = useFormik({
      initialValues: useMemo(
        () => ({
          month: '',
          day: '',
          year: ''
        }),
        []
      ),
      validationSchema: validation,
      validateOnMount: true
    });

    useImperativeHandle(ref, () => ({
      resetForm() {
        dateFormik.resetForm();
      }
    }));

    const selectableDays = (selectedMonth, selectedYear) => {
      const monthIndex = (month) => (month > 0 ? month - 1 : months.length + month - 1);
      const getDays = (month) => {
        return month !== undefined && month !== null
          ? months[monthIndex(month)].days
          : DAYS;
      };

      const selectedMonthNum = Number(selectedMonth) || null;

      const currentMonth = new Date().getMonth() + 1;
      const currentDay = new Date().getDate();
      const cutoffYear = sub(new Date(), { days: 15 }).getFullYear();
      const cutoffMonth = sub(new Date(), { days: 15 }).getMonth() + 1;
      const earliestDay =
        currentDay > daysInPastLimit
          ? currentDay - daysInPastLimit
          : getDays(cutoffMonth).length - (daysInPastLimit - currentDay);

      if (Number(selectedYear) !== cutoffYear) {
        return getDays(selectedMonthNum);
      }

      const currentMonthCutoffMonthAndSelected =
        selectedMonthNum === cutoffMonth &&
        cutoffMonth === currentMonth &&
        currentDay > daysInPastLimit;

      const previousMonthCutoffMonthAndSelected =
        selectedMonthNum === cutoffMonth &&
        cutoffMonth !== currentMonth &&
        currentDay <= daysInPastLimit;

      if (currentMonthCutoffMonthAndSelected) {
        return getDays(selectedMonthNum).slice(
          earliestDay,
          getDays(selectedMonthNum).length
        );
      }

      if (previousMonthCutoffMonthAndSelected) {
        return getDays(selectedMonthNum).slice(
          earliestDay,
          getDays(selectedMonthNum).length
        );
      }

      return getDays(selectedMonthNum);
    };

    const selectableMonths = (selectedYear) => {
      const currentDay = new Date().getDate();
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();
      const canCancelDaysInPreviousYear =
        currentMonth === 1 && currentDay < daysInPastLimit;
      if (selectedYear === `${currentYear}` && !canCancelDaysInPreviousYear) {
        return currentDay < daysInPastLimit
          ? months.slice(currentMonth - 2)
          : months.slice(currentMonth - 1);
      }
      if (Number(selectedYear) === currentYear - 1 && canCancelDaysInPreviousYear) {
        return [months[months.length - 1]];
      }
      return months;
    };

    useEffect(() => {
      const { year, month, day } = dateFormik.values;
      if (year && month && day) {
        // Set the values only when all are present to avoid invalid dates
        // and improve validations
        setFieldValue(name, `${year}-${month}-${day}`);
      }
    }, [dateFormik.values, setFieldValue, name]);

    useEffect(() => {
      const date = parse(values[name], 'yyyy-MM-dd', new Date());
      const isDateValid = isValid(date);

      if (isDateValid) {
        const parsedDate = values[name].split('-');
        dateFormik.setFieldValue('month', parsedDate[1]);
        dateFormik.setFieldValue('day', parsedDate[2]);
        dateFormik.setFieldValue('year', parsedDate[0]);
      }
    }, [values[name]]); // eslint-disable-line
    // We only need to check this on mount.

    const handleBlur = () => {
      if (formik) {
        formik.setTouched({ ...formik.touched, [name]: true });
      }
    };

    const renderMobileSelect = (
      <SelectContainer>
        <DefaultSelectWrapper
          variant={variant}
          isPlaceholder={!dateFormik.values.month}
          hasError={formik.errors.month}
        >
          {showCaret && <FaCaretDown />}
          <Label>Month</Label>
          <select
            name="month"
            disabled={disabled}
            {...dateFormik?.getFieldProps('month')}
            onBlur={handleBlur}
          >
            <option value="" disabled hidden>
              {' '}
            </option>
            {(daysInPastLimit
              ? selectableMonths(dateFormik.values.year)
              : getMonths(true)
            ).map((month, index) => (
              <option key={index} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </DefaultSelectWrapper>
        <DefaultSelectWrapper variant={variant} isPlaceholder={!dateFormik.values.day}>
          {showCaret && <FaCaretDown />}
          <Label>Day</Label>
          <select name="day" disabled={disabled} {...dateFormik?.getFieldProps('day')}>
            <option value="" disabled hidden>
              {' '}
            </option>
            {(daysInPastLimit
              ? selectableDays(dateFormik.values.month, dateFormik.values.year)
              : availableDays
            ).map((day, index) => (
              <option key={index} value={day.value}>
                {day.label}
              </option>
            ))}
          </select>
        </DefaultSelectWrapper>
        <DefaultSelectWrapper variant={variant} isPlaceholder={!dateFormik.values.year}>
          {showCaret && <FaCaretDown />}
          <Label>Year</Label>
          <select name="year" disabled={disabled} {...dateFormik?.getFieldProps('year')}>
            <option value="" disabled hidden>
              {' '}
            </option>
            {availableYears.map((year, index) => (
              <option key={index} value={year.value}>
                {year.label}
              </option>
            ))}
          </select>
        </DefaultSelectWrapper>
      </SelectContainer>
    );

    const renderDesktopSelect = (
      <SelectContainer>
        {disabled ? (
          <DisabledDate>
            {format(
              new Date(
                dateFormik.values.year,
                Number(dateFormik.values.month) - 1,
                dateFormik.values.day,
                0,
                0,
                0
              ),
              'MMMM d, yyyy'
            )}
          </DisabledDate>
        ) : (
          <>
            <StyledSelect
              formik={dateFormik}
              options={
                daysInPastLimit ? selectableMonths(dateFormik.values.year) : months
              }
              name="month"
              label="Month"
              disabled={disabled}
              variant={variant}
              showIndicator
            />
            <StyledSelect
              options={
                daysInPastLimit
                  ? selectableDays(dateFormik.values.month, dateFormik.values.year)
                  : availableDays
              }
              formik={dateFormik}
              name="day"
              disabled={disabled}
              variant={variant}
              label="Day"
              showIndicator
            />
            <StyledSelect
              options={availableYears}
              formik={dateFormik}
              name="year"
              disabled={disabled}
              variant={variant}
              label="Year"
              showIndicator
            />
          </>
        )}
      </SelectContainer>
    );

    return (
      <Container className={className}>
        {isTablet || isMobile ? renderMobileSelect : renderDesktopSelect}
      </Container>
    );
  }
);

DateInput.propTypes = {
  variant: PropTypes.oneOf(['white']),
  formik: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  showCaret: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  daysInPastLimit: PropTypes.number,
  availableDays: PropTypes.array,
  availableYears: PropTypes.array
};

DateInput.defaultProps = {
  variant: 'white'
};

export default DateInput;
